import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CountUp from 'react-countup';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { Users, TrendingDown, AlertTriangle, UserX, Info, CheckCircle, ArrowUp, ArrowDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { Skeleton } from '../ui/Skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'react-toastify';
import { ReactComponent as MonitorDownIcon } from '../../assets/icons/monitor-freccia-down.svg';
import { ReactComponent as MonitorUpIcon } from '../../assets/icons/monitor-freccia-up.svg';
import { ReactComponent as ContattareTelefonoIcon } from '../../assets/icons/contattare-telefono.svg';
import { DASHBOARD_BG_DARK, DASHBOARD_BG_LIGHT } from '../../constants/dashboardBackgrounds';

/** Padding orizzontale: cresce con lo schermo ma con cap per evitare overflow orizzontale */
const DASHBOARD_X_PAD = 'px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-12';

/** Numero che anima da 0 al valore al caricamento (count-up) */
function AnimatedNumber({ value, duration = 1.2, delay = 0, decimals = 0, suffix = '', prefix = '', className = '' }) {
    const isNumber = typeof value === 'number' && !Number.isNaN(value);
    if (!isNumber) return <span className={className}>-</span>;
    return (
        <span className={className}>
            <CountUp
                start={0}
                end={value}
                duration={duration}
                delay={delay}
                decimals={decimals}
                suffix={suffix}
                prefix={prefix}
                separator=","
            />
        </span>
    );
}

/** Fasce Indice Salute (metodo secchio): 80-100, 70-80, 50-70, 30-50, 0-30 */
function getIndiceSaluteRange(value) {
    if (value == null) return null;
    if (value >= 80) return { key: 'bucketFull' };
    if (value >= 70) return { key: 'capAlmostPerfect' };
    if (value >= 50) return { key: 'smallLeaks' };
    if (value >= 30) return { key: 'evidentLeak' };
    return { key: 'bucketHole' };
}

function clamp01(n) {
    if (Number.isNaN(n)) return 0;
    if (n < 0) return 0;
    if (n > 1) return 1;
    return n;
}

function hexToRgb(hex) {
    const h = hex.replace('#', '').trim();
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const int = Number.parseInt(full, 16);
    return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

function rgbToHex({ r, g, b }) {
    const toHex = (v) => v.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function lerp(a, b, t) {
    return a + (b - a) * t;
}

function mixHex(a, b, t) {
    const ca = hexToRgb(a);
    const cb = hexToRgb(b);
    return rgbToHex({
        r: Math.round(lerp(ca.r, cb.r, t)),
        g: Math.round(lerp(ca.g, cb.g, t)),
        b: Math.round(lerp(ca.b, cb.b, t)),
    });
}

function getIndiceSaluteLabelColor(value) {
    const v = Number(value);
    if (!Number.isFinite(v)) return undefined;
    const clamped = Math.max(0, Math.min(100, v));

    const RED = '#c9171a';
    const ORANGE = '#ea6849';
    const YELLOW = '#ffcc66';
    const GREEN = '#00cc99';

    if (clamped < 30) return mixHex(RED, ORANGE, clamp01(clamped / 30));
    if (clamped < 70) {
        // Mantieni il colore più "arancio" e vira verso giallo solo vicino a 70
        const t = clamp01((clamped - 30) / 40);
        return mixHex(ORANGE, YELLOW, t ** 4);
    }
    return mixHex(YELLOW, GREEN, clamp01((clamped - 70) / 30));
}

function getContinuityColor(pct) {
    const v = Number(pct);
    if (!Number.isFinite(v)) return undefined;
    const clamped = Math.max(0, Math.min(100, v));
    const RED = '#c9171a';
    const ORANGE = '#ea6849';
    const YELLOW = '#ffcc66';
    const GREEN = '#00cc99';

    if (clamped < 30) return mixHex(RED, ORANGE, clamp01(clamped / 30));
    if (clamped < 70) return mixHex(ORANGE, YELLOW, clamp01((clamped - 30) / 40));
    return mixHex(YELLOW, GREEN, clamp01((clamped - 70) / 30));
}

function stripPctRangePrefix(text) {
    if (!text) return '';
    // Es. "20-40% Clienti poco stabili" -> "Clienti poco stabili"
    return String(text).replace(/^\s*\d+\s*-\s*\d+\s*%?\s*/u, '');
}

/** Punto su arco superiore (stesso modello del tachimetro continuità in statistiche individuali) */
function continuityGaugePolarToXY(cx, cy, r, angleRad) {
    return {
        x: cx + r * Math.cos(angleRad),
        y: cy - r * Math.sin(angleRad),
    };
}

function continuityGaugeArcPath(cx, cy, r, startAngle, endAngle) {
    const start = continuityGaugePolarToXY(cx, cy, r, startAngle);
    const end = continuityGaugePolarToXY(cx, cy, r, endAngle);
    const delta = Math.abs(startAngle - endAngle);
    const largeArc = delta > Math.PI ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

function ContinuityGauge({ pct, label }) {
    const { isDark } = useTheme();
    const v = Number(pct);
    const clamped = Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : 0;
    const accent = getContinuityColor(clamped) || '#ffcc66';

    const cx = 100;
    const cy = 100;
    const r = 78;
    const strokeW = 14;
    /** 0% = sinistra (π), 100% = destra (0) */
    const angleForPct = (p) => Math.PI * (1 - p / 100);
    const needleAngle = angleForPct(clamped);
    const needleLen = r - 4;
    const nx = cx + needleLen * Math.cos(needleAngle);
    const ny = cy - needleLen * Math.sin(needleAngle);

    const segments = [
        { from: 0, to: 20, color: '#c9171a' },
        { from: 20, to: 40, color: '#ea6849' },
        { from: 40, to: 60, color: '#ffcc66' },
        { from: 60, to: 80, color: '#5fd9b3' },
        { from: 80, to: 100, color: '#00cc99' },
    ];

    return (
        <div className="w-full h-full flex flex-col items-center justify-center">
            <div className="w-[176px] sm:w-[200px] relative">
                <svg
                    viewBox="0 0 200 118"
                    className="w-full h-auto block max-h-[110px] sm:max-h-[125px]"
                    role="img"
                    aria-hidden
                >
                    {segments.map((seg) => (
                        <path
                            key={`${seg.from}-${seg.to}`}
                            d={continuityGaugeArcPath(cx, cy, r, angleForPct(seg.from), angleForPct(seg.to))}
                            fill="none"
                            stroke={seg.color}
                            strokeWidth={strokeW}
                            strokeLinecap="butt"
                        />
                    ))}
                    <line
                        x1={cx}
                        y1={cy}
                        x2={nx}
                        y2={ny}
                        stroke={isDark ? '#f3f4f6' : '#3a3a3a'}
                        strokeWidth={2}
                        strokeLinecap="round"
                        filter="drop-shadow(0 0 4px rgba(0,0,0,0.25))"
                    />
                    <circle
                        cx={cx}
                        cy={cy}
                        r={7}
                        fill={accent}
                        stroke="rgba(255,255,255,0.18)"
                        strokeWidth={3}
                    />
                </svg>
            </div>
            <div className="mt-2 text-center">
                <div
                    className={`font-open-sans font-extrabold text-[30px] sm:text-[34px] leading-none ${!isDark ? 'text-[#3a3a3a]' : ''}`}
                    style={isDark ? { color: accent } : undefined}
                >
                    <CountUp start={0} end={clamped} duration={1.0} delay={0.2} decimals={0} suffix="%" />
                </div>
                {label ? (
                    <div className="mt-1 font-open-sans font-bold tracking-wide text-[12px] sm:text-[13px] text-[#3a3a3a] dark:text-white/85">
                        {stripPctRangePrefix(label)}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

/** Fasce Attività di Allenamento: 80-100, 60-80, 40-60, 20-40, 0-20 */
function getAttivitaBandKey(pct) {
    if (pct == null || pct < 0) return null;
    if (pct >= 80) return 'abitudineForte';
    if (pct >= 60) return 'buonaStabilita';
    if (pct >= 40) return 'rischioRallentamento';
    if (pct >= 20) return 'clientiPocoStabili';
    return 'palestraMoltoFragile';
}

/** Una riga: label, valore; la fascia (band) si mostra solo per Indice di continuità (format percent) */
function AttivitaIndiceRow({ label, value, format, t }) {
    if (value == null) return null;
    let displayValue = '';
    if (format === 'workoutsPerWeek') {
        displayValue = value.toFixed(1) + ' ' + t('gym.dashboard.attivitaAllenamento.allSettimana');
    } else {
        displayValue = value.toFixed(0) + '%';
    }
    const pct = format === 'percent' ? value : null;
    const bandKey = pct != null ? getAttivitaBandKey(pct) : null;
    const bandLabel = bandKey ? t(`gym.dashboard.attivitaAllenamento.bands.${bandKey}`) : null;
    return (
        <div className="min-w-0">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wide">{label}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1 tabular-nums">
                {format === 'workoutsPerWeek' ? (
                    <>
                        <CountUp start={0} end={value} duration={1} delay={0.35} decimals={1} />
                        {' ' + t('gym.dashboard.attivitaAllenamento.allSettimana')}
                    </>
                ) : (
                    <>
                        <CountUp start={0} end={value} duration={1} delay={0.4} decimals={0} suffix="%" />
                    </>
                )}
            </p>
            {format === 'percent' && bandLabel && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{bandLabel}</p>
            )}
        </div>
    );
}

/**
 * Indice Salute (0-100): valore e testi a sinistra, secchio (immagine grafica) a destra.
 * Dimensioni responsive con clamp in base al viewport.
 */
function IndiceSaluteSecchio({ value, t }) {
    const range = getIndiceSaluteRange(value);
    const v = Number(value);
    const clamped = Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : 0;
    const bucketStep = clamped < 20 ? 0 : clamped < 40 ? 20 : clamped < 60 ? 40 : clamped < 80 ? 60 : clamped < 100 ? 80 : 100;
    const bucketImgSrc = `/images/buckets/Secchio_${bucketStep}.png`;
    return (
        <div className="flex w-full min-w-0 max-w-full flex-col items-start gap-5 min-[520px]:flex-row min-[520px]:items-center min-[520px]:gap-5">
            {/* Centra il testo nello spazio disponibile (tra sidebar e secchio) */}
            <div className="min-w-0 w-full min-[520px]:flex-1 min-[520px]:flex min-[520px]:justify-center">
                <div className="min-w-0 max-w-[420px] flex flex-col items-center text-center gap-3">
                <h2 className="font-open-sans font-semibold tracking-wide text-gray-900 dark:text-white whitespace-nowrap text-center text-[clamp(18px,1.6vw,22px)]">
                    {t('gym.dashboard.indiceSalute.title')}
                </h2>
                <div className="min-w-0">
                    <div className="font-open-sans tabular-nums leading-none tracking-tight">
                        <span className="inline-flex items-baseline gap-2">
                            <span className="font-extrabold text-[clamp(78px,8vw,110px)] text-[#393083] dark:text-white">
                                <CountUp start={0} end={value} duration={1.2} delay={0.3} decimals={0} />
                            </span>
                            <span className="text-[#9e96c7] dark:text-[#aaa3d6] font-semibold leading-none text-[clamp(24px,2.8vw,38px)]">
                                /100
                            </span>
                        </span>
                    </div>
                </div>
                <div className="min-w-0">
                    <div
                        className="font-open-sans font-bold leading-tight text-center text-[clamp(20px,2.4vw,30px)]"
                        style={{ color: getIndiceSaluteLabelColor(value) }}
                    >
                        {range ? t(`gym.dashboard.indiceSalute.ranges.${range.key}`) : '-'}
                    </div>
                </div>
                </div>
            </div>

            <div className="flex shrink-0 items-center justify-center self-center min-[520px]:self-auto">
                <img
                    src={bucketImgSrc}
                    alt={t('gym.dashboard.indiceSalute.title')}
                    className="object-contain select-none"
                    style={{
                        width: 'clamp(14.5rem, 22vw, 19rem)',
                        height: 'clamp(14.5rem, 22vw, 19rem)'
                    }}
                    draggable={false}
                />
            </div>
        </div>
    );
}

/** Variazione % vs media del mese precedente: una sola riga affianco al valore (testo lungo nel title nativo) */
function TrendBadge({ value, positiveIsGood, t }) {
    if (value == null) return null;
    const isPositive = value >= 0;
    const isGood = positiveIsGood ? isPositive : !isPositive;
    const Icon = isPositive ? ArrowUp : ArrowDown;
    const trendTitle = t('gym.dashboard.cards.trendVsMonth', { defaultValue: 'Rispetto al mese scorso' });
    return (
        <span
            className="inline-flex flex-nowrap items-center shrink-0"
            title={trendTitle}
        >
            <span
                className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium ${
                    isGood
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200'
                }`}
            >
                <Icon size={12} />
                {isPositive ? '+' : ''}{value}%
            </span>
        </span>
    );
}

/**
 * A riposo: stessa altezza tra le card.
 * In hover/focus: solo questa card cresce - usare hover:/focus-within: sull’elemento card, NON group-hover:,
 * perché group-hover applica lo stile ai discendenti del .group in hover; sull’elemento che ha anche `group`
 * l’altezza group-hover:h-auto non viene applicata e la card resta tagliata (overflow-hidden).
 */
const DASHBOARD_TOP_KPI_CARD_BASE =
    'group flex w-full flex-col gap-1 min-h-[124px] sm:min-h-[132px] h-[124px] sm:h-[132px] hover:h-auto focus-within:h-auto overflow-hidden bg-[#eeebf5] dark:bg-gray-950 rounded-2xl px-3 py-3 sm:px-4 sm:py-3 border-2 shadow-md transition-[box-shadow,transform] duration-200 hover:shadow-lg hover:scale-[1.01]';

/** Blocco descrizione in fondo alla card: collassato a riposo; si espande in hover (la card padre cresce in altezza) */
function DashboardKpiCardInnerDesc({ children, scrollable }) {
    return (
        <div
            role="note"
            className="shrink-0 border-t border-transparent pt-1.5 transition-[max-height,opacity,border-color] duration-200 ease-out max-h-0 overflow-hidden opacity-0 group-hover:max-h-[10rem] sm:group-hover:max-h-[11rem] group-hover:opacity-100 group-hover:border-white/20 dark:group-hover:border-white/10"
        >
            <div
                className={`font-open-sans font-light text-[11px] sm:text-[12px] text-gray-600 dark:text-[#c9c5e8] leading-snug ${scrollable ? 'max-h-[9.5rem] overflow-y-auto pr-0.5' : ''}`}
            >
                {children}
            </div>
        </div>
    );
}

/** Spacer legacy: le card KPI ora hanno altezza fissa; se resta un riferimento (HMR/bundle) non deve rompere il runtime */
function DashboardKpiTrendRowSpacer() {
    return null;
}

const KPI_SKELETON_BORDER_CLASSES = [
    'border-[#00A3FF]',
    'border-[#00cc99]',
    'border-[#ffcc66]',
    'border-[#ea6849]',
    'border-[#c9171a]',
];

function DashboardKpiCardSkeleton({ borderClass }) {
    return (
        <div className={`${DASHBOARD_TOP_KPI_CARD_BASE} ${borderClass}`}>
            <Skeleton className="h-3.5 w-20 sm:w-24 bg-gray-300/80 dark:bg-white/20" />
            <Skeleton className="h-9 sm:h-10 w-14 sm:w-16 bg-gray-300/80 dark:bg-white/25" />
        </div>
    );
}

/** Skeleton moduli: stessa griglia della dashboard caricata (secchio, allenamenti, segnali, da contattare) */
function DashboardModulesSkeleton({ isMobile }) {
    const moduleShell =
        'rounded-2xl overflow-hidden flex flex-col border border-[#dad8e5] dark:border-white/10 bg-[#eeebf5] dark:bg-gray-900 shadow-lg dark:shadow-black/35';
    const moduleHeader = 'px-5 bg-[#dad8e5] dark:bg-gray-800 h-14 sm:h-16 flex items-center';
    const gridGap = isMobile ? 'gap-3' : 'gap-4 md:gap-6 xl:gap-8';

    return (
        <div className={`w-full max-w-full min-w-0 ${isMobile ? 'p-3' : 'p-4 sm:p-5 lg:p-6 xl:p-8'}`}>
            <div className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 items-stretch ${gridGap}`}>
                <div className={`md:col-span-2 xl:col-span-2 min-h-[clamp(210px,26vh,300px)] flex items-center justify-center px-6 sm:px-8 lg:px-10 py-4 sm:py-5`}>
                    <Skeleton className="h-36 w-36 sm:h-40 sm:w-40 rounded-full bg-gray-300/70 dark:bg-white/15" />
                </div>
                <div className={`md:col-span-2 xl:col-span-2 min-h-[clamp(210px,26vh,300px)] ${moduleShell}`}>
                    <div className={moduleHeader}>
                        <Skeleton className="h-5 w-36 bg-gray-400/60 dark:bg-white/20" />
                    </div>
                    <div className="px-5 pt-4 pb-5 flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                            <Skeleton className="h-16 w-full rounded-xl bg-gray-300/60 dark:bg-white/10" />
                            <Skeleton className="h-16 w-full rounded-xl bg-gray-300/60 dark:bg-white/10" />
                            <Skeleton className="h-20 w-full rounded-xl bg-gray-300/60 dark:bg-white/10" />
                        </div>
                        <div className="flex items-center justify-center">
                            <Skeleton className="h-28 w-full max-w-[200px] rounded-xl bg-gray-300/60 dark:bg-white/10" />
                        </div>
                    </div>
                </div>
            </div>
            <div className={`grid grid-cols-1 md:grid-cols-2 mt-5 md:mt-6 items-stretch ${gridGap}`}>
                {[1, 2].map((i) => (
                    <div key={i} className={`min-h-[140px] ${moduleShell}`}>
                        <div className={moduleHeader}>
                            <Skeleton className="h-5 w-44 bg-gray-400/60 dark:bg-white/20" />
                        </div>
                        <div className="px-5 pt-4 pb-5 flex-1 flex items-center justify-center">
                            <Skeleton className="h-20 w-full rounded-xl bg-gray-300/60 dark:bg-white/10" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/** Layout di caricamento allineato alla dashboard attuale (evita flash UI legacy) */
function GymDashboardLoadingSkeleton({ isMobile, isDark }) {
    return (
        <div
            className={`min-h-screen w-full max-w-full overflow-x-hidden ${isMobile ? 'min-w-0 text-[90%]' : ''}`}
            style={{ background: isDark ? DASHBOARD_BG_DARK : DASHBOARD_BG_LIGHT }}
            aria-busy="true"
            aria-live="polite"
        >
            <div className="w-full max-w-full">
                <div
                    className={`w-full ${isMobile ? 'px-2 py-5' : `${DASHBOARD_X_PAD} py-6`} flex items-start gap-2 sm:gap-3 lg:gap-4 min-h-[216px] sm:min-h-[240px]`}
                >
                    <div className="hidden sm:flex shrink-0 h-full items-center">
                        <Skeleton className="h-[120px] w-[120px] lg:h-[168px] lg:w-[140px] rounded-lg bg-gray-300/70 dark:bg-white/15" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div
                            className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 items-start ${isMobile ? 'gap-1.5' : 'gap-2 sm:gap-3 lg:gap-4'}`}
                        >
                            {KPI_SKELETON_BORDER_CLASSES.map((borderClass) => (
                                <DashboardKpiCardSkeleton key={borderClass} borderClass={borderClass} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className={isMobile ? 'w-full px-2 pt-3 pb-6' : `w-full ${DASHBOARD_X_PAD} pt-3 pb-10`}>
                <DashboardModulesSkeleton isMobile={isMobile} />
            </div>
        </div>
    );
}

/** In DEV e PROD gli hint vengono mostrati come tooltip al passaggio del mouse invece che inline */
const SHOW_HINT_AS_TOOLTIP = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'production';

/** Intestazione card: titolo + hint come tooltip (dev/prod) o come testo sotto (altri env) */
function CardHeader({ title, hint }) {
    const showTooltip = SHOW_HINT_AS_TOOLTIP && hint;
    return (
        <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-1.5">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h2>
                {showTooltip && (
                    <span
                        className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 cursor-help"
                        title={hint}
                    >
                        <Info size={14} />
                    </span>
                )}
            </div>
            {!showTooltip && hint && (
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 leading-tight">{hint}</p>
            )}
        </div>
    );
}

/** Blocco metrica con label, valore e descrizione in tooltip (dev/prod) o sotto (altri env) */
function MetricWithTooltip({ label, value, description }) {
    const showTooltip = SHOW_HINT_AS_TOOLTIP && description;
    return (
        <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wide">{label}</p>
                {showTooltip && (
                    <span
                        className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 cursor-help"
                        title={description}
                    >
                        <Info size={12} />
                    </span>
                )}
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1 tabular-nums">
                {typeof value === 'number' ? (
                    <CountUp start={0} end={value} duration={1} delay={0.55} decimals={0} />
                ) : (
                    value
                )}
            </p>
            {!showTooltip && description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-snug">{description}</p>
            )}
        </div>
    );
}

/**
 * Dashboard palestra - ricostruita da zero.
 * Dati da API gym_dashboard.php?action=snapshot (snapshot precalcolati ogni sera alle 23).
 */
const MOBILE_DASHBOARD_BREAKPOINT = '(max-width: 767px)';

const GymDashboard = () => {
    const { t } = useTranslation();
    const { isDark } = useTheme();
    const { user, getAuthenticatedApi } = useAuth();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(MOBILE_DASHBOARD_BREAKPOINT);
    const [snapshot, setSnapshot] = useState(null);
    const [gymData, setGymData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [logoError, setLogoError] = useState(false);
    const [workoutsInCorso, setWorkoutsInCorso] = useState(null);
    const [workoutsDoneToday, setWorkoutsDoneToday] = useState(null);
    const [daContattareOpen, setDaContattareOpen] = useState(false);
    const [listaDaContattare, setListaDaContattare] = useState([]);
    const [loadingListaDaContattare, setLoadingListaDaContattare] = useState(false);
    const [submittingContact, setSubmittingContact] = useState(null);
    const [formDaContattare, setFormDaContattare] = useState({});
    const [listCardModal, setListCardModal] = useState(null);
    const [listCardData, setListCardData] = useState([]);
    const [loadingListCard, setLoadingListCard] = useState(false);

    useEffect(() => {
        if (!listCardModal) return;
        const actionMap = {
            rallentamento: 'lista_in_rallentamento',
            rischio: 'lista_rischio_abbandono',
            frequenza_in_calo: 'lista_frequenza_in_calo',
            riattivati_mese: 'lista_riattivati_mese',
        };
        const action = actionMap[listCardModal];
        if (!action) return;
        let cancelled = false;
        const api = getAuthenticatedApi();
        setLoadingListCard(true);
        setListCardData([]);
        api.get(`/gym_dashboard.php?action=${action}`)
            .then((res) => {
                if (cancelled) return;
                if (res.data?.success && Array.isArray(res.data.lista)) {
                    setListCardData(res.data.lista);
                    const count = res.data.lista.length;
                    setSnapshot((s) => {
                        if (!s) return s;
                        const total = s.clienti_totali > 0 ? s.clienti_totali : 1;
                        if (action === 'lista_rischio_abbandono') {
                            return { ...s, clienti_rischio_abbandono: count, clienti_rischio_abbandono_pct: total ? (count / total) * 100 : null };
                        }
                        if (action === 'lista_in_rallentamento') {
                            return { ...s, clienti_in_rallentamento: count, clienti_in_rallentamento_pct: total ? (count / total) * 100 : null };
                        }
                        if (action === 'lista_frequenza_in_calo') {
                            return { ...s, clienti_frequenza_in_calo: count };
                        }
                        if (action === 'lista_riattivati_mese') {
                            return { ...s, clienti_riattivati_mese: count };
                        }
                        return s;
                    });
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingListCard(false);
            });
        return () => { cancelled = true; };
    }, [listCardModal, getAuthenticatedApi]);

    useEffect(() => {
        if (!daContattareOpen) return;
        let cancelled = false;
        const api = getAuthenticatedApi();
        setLoadingListaDaContattare(true);
        setListaDaContattare([]);
        api.get('/gym_dashboard.php?action=lista_da_contattare')
            .then((res) => {
                if (cancelled) return;
                if (res.data?.success && Array.isArray(res.data.lista)) {
                    setListaDaContattare(res.data.lista);
                    const initial = {};
                    res.data.lista.forEach((item) => {
                        initial[item.user_id] = { tipo_azione: '', note: '' };
                    });
                    setFormDaContattare(initial);
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingListaDaContattare(false);
            });
        return () => { cancelled = true; };
    }, [daContattareOpen, getAuthenticatedApi]);

    const handleRegistraContatto = async (user_id) => {
        const fd = formDaContattare[user_id] || { tipo_azione: '', note: '' };
        if (!fd.tipo_azione) {
            toast.error(t('gym.dashboard.daContattareModal.selezionaAzione'));
            return;
        }
        const api = getAuthenticatedApi();
        setSubmittingContact(user_id);
        try {
            const res = await api.post('/gym_dashboard.php?action=registra_contatto', {
                user_id,
                tipo_azione: fd.tipo_azione,
                note: fd.note || ''
            });
            if (res.data?.success) {
                setListaDaContattare((prev) => {
                    const next = prev.filter((u) => u.user_id !== user_id);
                    setSnapshot((s) => (s ? { ...s, clienti_da_contattare: next.length } : null));
                    return next;
                });
                const next = { ...formDaContattare };
                delete next[user_id];
                setFormDaContattare(next);
                toast.success(t('gym.dashboard.daContattareModal.registra') + ' ✓');
            } else {
                toast.error(res.data?.error || 'Errore');
            }
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Errore di rete');
        } finally {
            setSubmittingContact(null);
        }
    };

    useEffect(() => {
        let cancelled = false;
        const api = getAuthenticatedApi();

        const load = async () => {
            try {
                const [
                    snapRes,
                    gymsRes,
                    listaRischioRes,
                    listaRallentamentoRes,
                    listaCaloRes,
                    listaRiattivatiRes,
                ] = await Promise.all([
                    api.get('/gym_dashboard.php?action=snapshot'),
                    api.get('/gyms.php'),
                    api.get('/gym_dashboard.php?action=lista_rischio_abbandono'),
                    api.get('/gym_dashboard.php?action=lista_in_rallentamento'),
                    api.get('/gym_dashboard.php?action=lista_frequenza_in_calo'),
                    api.get('/gym_dashboard.php?action=lista_riattivati_mese'),
                ]);

                if (cancelled) return;
                if (snapRes.data?.success && snapRes.data?.snapshot) {
                    const snap = snapRes.data.snapshot;
                    const total = snap.clienti_totali > 0 ? snap.clienti_totali : 1;
                    const rischioCount = listaRischioRes.data?.success && Array.isArray(listaRischioRes.data.lista) ? listaRischioRes.data.lista.length : snap.clienti_rischio_abbandono;
                    const rallentamentoCount = listaRallentamentoRes.data?.success && Array.isArray(listaRallentamentoRes.data.lista) ? listaRallentamentoRes.data.lista.length : snap.clienti_in_rallentamento;
                    const caloCount = listaCaloRes.data?.success && Array.isArray(listaCaloRes.data.lista) ? listaCaloRes.data.lista.length : snap.clienti_frequenza_in_calo;
                    const riattivatiCount = listaRiattivatiRes.data?.success && Array.isArray(listaRiattivatiRes.data.lista) ? listaRiattivatiRes.data.lista.length : snap.clienti_riattivati_mese;
                    setSnapshot({
                        ...snap,
                        clienti_rischio_abbandono: rischioCount,
                        clienti_rischio_abbandono_pct: total ? (rischioCount / total) * 100 : snap.clienti_rischio_abbandono_pct,
                        clienti_in_rallentamento: rallentamentoCount,
                        clienti_in_rallentamento_pct: total ? (rallentamentoCount / total) * 100 : snap.clienti_in_rallentamento_pct,
                        clienti_frequenza_in_calo: caloCount,
                        clienti_riattivati_mese: riattivatiCount,
                    });
                }
                if (gymsRes.data?.length > 0) {
                    setGymData(gymsRes.data[0]);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.response?.data?.error || err.message || t('gym.dashboard.errorLoadingData'));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [getAuthenticatedApi]);

    const fetchWorkoutsInCorso = async () => {
        try {
            const api = getAuthenticatedApi();
            const res = await api.get('/gym_dashboard.php?action=workouts_in_corso');
            if (res.data?.success) {
                if (typeof res.data.count === 'number') setWorkoutsInCorso(res.data.count);
                if (typeof res.data.workouts_today === 'number') setWorkoutsDoneToday(res.data.workouts_today);
            }
        } catch {
            // non bloccare l'UI; i contatori restano ai valori precedenti o null
        }
    };

    useEffect(() => {
        fetchWorkoutsInCorso();
        const REFRESH_MS = 60 * 1000;
        const interval = setInterval(fetchWorkoutsInCorso, REFRESH_MS);
        return () => clearInterval(interval);
    }, [getAuthenticatedApi]);

    if (loading) {
        return <GymDashboardLoadingSkeleton isMobile={isMobile} isDark={isDark} />;
    }

    if (error) {
        return (
            <div
                className="flex min-h-[50vh] w-full max-w-full items-center justify-center overflow-x-hidden"
                style={{ background: isDark ? DASHBOARD_BG_DARK : DASHBOARD_BG_LIGHT }}
            >
                <div className="text-red-600 dark:text-red-400">{error}</div>
            </div>
        );
    }

    const logoUrl = gymData?.logo_filename
        ? `/api/serve_image.php?filename=${encodeURIComponent(gymData.logo_filename)}&type=logo`
        : null;

    return (
        <div
            className={`min-h-screen w-full max-w-full overflow-x-hidden ${isMobile ? 'min-w-0 text-[90%]' : ''}`}
            style={{ background: isDark ? DASHBOARD_BG_DARK : DASHBOARD_BG_LIGHT }}
        >
            {/* Area hero: contatori con sfondo chiaro #eeebf5 solo in tema chiaro */}
            <div className="w-full max-w-full">
                <div className={`w-full ${isMobile ? 'px-2 py-5' : `${DASHBOARD_X_PAD} py-6`} flex items-start gap-2 sm:gap-3 lg:gap-4 min-h-[216px] sm:min-h-[240px] overflow-visible`}>
                    {/* Logo a sinistra: stessa altezza delle card indicatori */}
                    <div className="hidden sm:flex shrink-0 h-full items-center">
                        <div className="h-full flex items-center justify-center px-1">
                            {logoUrl && !logoError ? (
                                <img
                                    src={logoUrl}
                                    alt={gymData?.name || t('gym.dashboard.logoAlt')}
                                    className="object-contain max-h-[168px] w-auto"
                                    onError={() => setLogoError(true)}
                                />
                            ) : (
                                <div className="text-center text-blue-900 dark:text-white font-bold whitespace-nowrap">
                                    {gymData?.name || t('gym.dashboard.gymNameFallback')}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Indicatori */}
                    <div className="flex-1 min-w-0">
                        <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 items-start ${isMobile ? 'gap-1.5' : 'gap-2 sm:gap-3 lg:gap-4'}`}>
                            {/* Card - Nuovi clienti (prima dei clienti attivi) */}
                            <div className={`${DASHBOARD_TOP_KPI_CARD_BASE} border-[#00A3FF]`}>
                                <div className="font-open-sans font-normal text-[13px] sm:text-[14px] text-[#3a3a3a] dark:text-white leading-tight shrink-0">
                                    {t('gym.dashboard.cards.nuoviClienti', { defaultValue: 'Nuovi clienti' })}
                                </div>
                                {snapshot && typeof snapshot.clienti_nuovi === 'number' ? (
                                    <>
                                        <div className="shrink-0 flex min-h-[2.75rem] sm:min-h-[3rem] items-baseline gap-2 flex-nowrap">
                                            <AnimatedNumber
                                                value={snapshot.clienti_nuovi}
                                                duration={1.2}
                                                delay={0.04}
                                                className="font-open-sans font-bold text-4xl sm:text-[44px] leading-none text-[#372d83] dark:text-white shrink-0"
                                            />
                                        </div>
                                        <DashboardKpiCardInnerDesc>
                                            {t('gym.dashboard.cards.nuoviClientiDesc', {
                                                defaultValue:
                                                    'Iscrizione e abbonamento entro 30 giorni, meno di 5 allenamenti negli ultimi 60 giorni di apertura. Conteggio live (senza %).',
                                            })}
                                        </DashboardKpiCardInnerDesc>
                                    </>
                                ) : (
                                    <p className="font-open-sans font-light text-[12px] sm:text-[13px] text-gray-600 dark:text-[#9492a9] flex-1 flex items-center">
                                        {t('gym.dashboard.cards.dataUnavailable')}
                                    </p>
                                )}
                            </div>
                            {/* Card - Clienti attivi */}
                            <div className={`${DASHBOARD_TOP_KPI_CARD_BASE} border-[#00cc99]`}>
                                <div className="font-open-sans font-normal text-[13px] sm:text-[14px] text-[#3a3a3a] dark:text-white leading-tight shrink-0">
                                    {t('gym.dashboard.cards.activeClients')}
                                </div>
                                {snapshot ? (
                                    <>
                                        <div className="shrink-0 flex min-h-[2.75rem] sm:min-h-[3rem] items-baseline gap-2 flex-nowrap min-w-0">
                                            <AnimatedNumber
                                                value={snapshot.clienti_attivi_7d}
                                                duration={1.2}
                                                delay={0}
                                                className="font-open-sans font-bold text-4xl sm:text-[44px] leading-none text-[#372d83] dark:text-white shrink-0"
                                            />
                                            <span className="font-open-sans font-light text-[12px] sm:text-[13px] text-gray-600 dark:text-[#9492a9] shrink-0">
                                                (<AnimatedNumber value={snapshot.clienti_attivi_7d_pct} duration={1} delay={0.15} decimals={0} suffix="%" className="inline" />)
                                            </span>
                                            {snapshot.clienti_attivi_trend_pct != null && (
                                                <TrendBadge value={snapshot.clienti_attivi_trend_pct} positiveIsGood t={t} />
                                            )}
                                        </div>
                                        <DashboardKpiCardInnerDesc scrollable>
                                            {t('gym.dashboard.cards.activeClientsDesc', {
                                                defaultValue:
                                                    'Clients with an active subscription who train in line with their usual pattern and have been in recently (counted on days your gym is open).',
                                            })}
                                        </DashboardKpiCardInnerDesc>
                                    </>
                                ) : (
                                    <p className="font-open-sans font-light text-[12px] sm:text-[13px] text-gray-600 dark:text-[#9492a9] flex-1 flex items-center">
                                        {t('gym.dashboard.cards.dataUpdating')}
                                    </p>
                                )}
                            </div>
                            {/* Card - In Rallentamento (arancione) - cliccabile per lista */}
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={() => snapshot && typeof snapshot.clienti_in_rallentamento === 'number' && setListCardModal('rallentamento')}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); snapshot && typeof snapshot.clienti_in_rallentamento === 'number' && setListCardModal('rallentamento'); } }}
                                className={`${DASHBOARD_TOP_KPI_CARD_BASE} border-[#ffcc66] ${snapshot != null ? 'cursor-pointer' : ''}`}
                            >
                                <div className="font-open-sans font-normal text-[13px] sm:text-[14px] text-[#3a3a3a] dark:text-white leading-tight shrink-0">
                                    {t('gym.dashboard.cards.inRallentamento')}
                                </div>
                                {snapshot && typeof snapshot.clienti_in_rallentamento === 'number' && snapshot.clienti_totali > 0 ? (
                                    <>
                                        <div className="shrink-0 flex min-h-[2.75rem] sm:min-h-[3rem] items-baseline gap-2 flex-nowrap min-w-0">
                                            <AnimatedNumber
                                                value={snapshot.clienti_in_rallentamento}
                                                duration={1.2}
                                                delay={0.08}
                                                className="font-open-sans font-bold text-4xl sm:text-[44px] leading-none text-[#372d83] dark:text-white shrink-0"
                                            />
                                            <span className="font-open-sans font-light text-[12px] sm:text-[13px] text-gray-600 dark:text-[#9492a9] shrink-0">
                                                ({snapshot.clienti_in_rallentamento_pct != null ? (
                                                    <AnimatedNumber value={snapshot.clienti_in_rallentamento_pct} duration={1} delay={0.2} decimals={0} suffix="%" className="inline" />
                                                ) : '-'})
                                            </span>
                                            {snapshot.clienti_in_rallentamento_trend_pct != null && (
                                                <TrendBadge value={snapshot.clienti_in_rallentamento_trend_pct} positiveIsGood={false} t={t} />
                                            )}
                                        </div>
                                        <DashboardKpiCardInnerDesc scrollable>
                                            {t('gym.dashboard.cards.inRallentamentoDesc', {
                                                defaultValue:
                                                    'They still come, but less often than before-a quick message or check-in usually gets them back on track.',
                                            })}
                                        </DashboardKpiCardInnerDesc>
                                    </>
                                ) : (
                                    <p className="font-open-sans font-light text-[12px] sm:text-[13px] text-gray-600 dark:text-[#9492a9] flex-1 flex items-center">
                                        {t('gym.dashboard.cards.dataUnavailable')}
                                    </p>
                                )}
                            </div>
                            {/* Card 3 - A Rischio Abbandono (rosso chiaro) - cliccabile per lista */}
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={() => snapshot && typeof snapshot.clienti_rischio_abbandono === 'number' && setListCardModal('rischio')}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); snapshot && typeof snapshot.clienti_rischio_abbandono === 'number' && setListCardModal('rischio'); } }}
                                className={`${DASHBOARD_TOP_KPI_CARD_BASE} border-[#ea6849] ${snapshot != null ? 'cursor-pointer' : ''}`}
                            >
                                <div className="font-open-sans font-normal text-[13px] sm:text-[14px] text-[#3a3a3a] dark:text-white leading-tight shrink-0">
                                    {t('gym.dashboard.cards.rischioAbbandono')}
                                </div>
                                {snapshot && typeof snapshot.clienti_rischio_abbandono === 'number' && snapshot.clienti_totali > 0 ? (
                                    <>
                                        <div className="shrink-0 flex min-h-[2.75rem] sm:min-h-[3rem] items-baseline gap-2 flex-nowrap min-w-0">
                                            <AnimatedNumber
                                                value={snapshot.clienti_rischio_abbandono}
                                                duration={1.2}
                                                delay={0.16}
                                                className="font-open-sans font-bold text-4xl sm:text-[44px] leading-none text-[#372d83] dark:text-white shrink-0"
                                            />
                                            <span className="font-open-sans font-light text-[12px] sm:text-[13px] text-gray-600 dark:text-[#9492a9] shrink-0">
                                                ({snapshot.clienti_rischio_abbandono_pct != null ? (
                                                    <AnimatedNumber value={snapshot.clienti_rischio_abbandono_pct} duration={1} delay={0.25} decimals={0} suffix="%" className="inline" />
                                                ) : '-'})
                                            </span>
                                            {snapshot.clienti_rischio_abbandono_trend_pct != null && (
                                                <TrendBadge value={snapshot.clienti_rischio_abbandono_trend_pct} positiveIsGood={false} t={t} />
                                            )}
                                        </div>
                                        <DashboardKpiCardInnerDesc scrollable>
                                            {t('gym.dashboard.cards.rischioAbbandonoDesc', {
                                                defaultValue:
                                                    'Worth a call: they have not been in for a while or their attendance has dropped sharply; without follow-up they may drift away.',
                                            })}
                                        </DashboardKpiCardInnerDesc>
                                    </>
                                ) : (
                                    <p className="font-open-sans font-light text-[12px] sm:text-[13px] text-gray-600 dark:text-[#9492a9] flex-1 flex items-center">
                                        {t('gym.dashboard.cards.dataUnavailable')}
                                    </p>
                                )}
                            </div>

                            {/* Card 4 - Clienti inattivi (grigio/slate) */}
                            <div className={`${DASHBOARD_TOP_KPI_CARD_BASE} border-[#c9171a]`}>
                                <div className="font-open-sans font-normal text-[13px] sm:text-[14px] text-[#3a3a3a] dark:text-white leading-tight shrink-0">
                                    {t('gym.dashboard.cards.inattivi')}
                                </div>
                                {snapshot && typeof snapshot.clienti_inattivi === 'number' && snapshot.clienti_totali > 0 ? (
                                    <>
                                        <div className="shrink-0 flex min-h-[2.75rem] sm:min-h-[3rem] items-baseline gap-2 flex-nowrap min-w-0">
                                            <AnimatedNumber
                                                value={snapshot.clienti_inattivi}
                                                duration={1.2}
                                                delay={0.24}
                                                className="font-open-sans font-bold text-4xl sm:text-[44px] leading-none text-[#372d83] dark:text-white shrink-0"
                                            />
                                            <span className="font-open-sans font-light text-[12px] sm:text-[13px] text-gray-600 dark:text-[#9492a9] shrink-0">
                                                ({snapshot.clienti_inattivi_pct != null ? (
                                                    <AnimatedNumber value={snapshot.clienti_inattivi_pct} duration={1} delay={0.3} decimals={0} suffix="%" className="inline" />
                                                ) : '-'})
                                            </span>
                                            {snapshot.clienti_inattivi_trend_pct != null && (
                                                <TrendBadge value={snapshot.clienti_inattivi_trend_pct} positiveIsGood={false} t={t} />
                                            )}
                                        </div>
                                        <DashboardKpiCardInnerDesc scrollable>
                                            {t('gym.dashboard.cards.inattiviDesc', {
                                                defaultValue:
                                                    'No recorded workout for a long time (based on open days). Only clients whose subscription is still active.',
                                            })}
                                        </DashboardKpiCardInnerDesc>
                                    </>
                                ) : (
                                    <p className="font-open-sans font-light text-[12px] sm:text-[13px] text-gray-600 dark:text-[#9492a9] flex-1 flex items-center">
                                        {t('gym.dashboard.cards.dataUnavailable')}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sezione moduli: larghezza piena, padding X responsive (WEBAPP-033 mobile compatto). */}
            <div className={isMobile ? 'w-full px-2 pt-3 pb-6' : `w-full ${DASHBOARD_X_PAD} pt-3 pb-10`}>
                <div className={`w-full max-w-full min-w-0 ${isMobile ? 'p-3' : 'p-4 sm:p-5 lg:p-6 xl:p-8'}`}>
                    <p className="sr-only">{t('gym.dashboard.mainModulesLabel')}</p>
                    <div className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 items-stretch ${isMobile ? 'gap-3' : 'gap-4 md:gap-6 xl:gap-8'}`}>
                        {/* Card 1: Livello di tenuta - più larga su desktop (come blocco “stato”) */}
                        <motion.section
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0 }}
                            className="md:col-span-2 xl:col-span-2 h-full min-h-[clamp(210px,26vh,300px)] flex flex-col justify-center px-6 sm:px-8 lg:px-10 py-4 sm:py-5"
                        >
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-1 flex-col justify-center">
                                    {snapshot != null && typeof snapshot.indice_salute === 'number' ? (
                                        <IndiceSaluteSecchio value={snapshot.indice_salute} t={t} />
                                    ) : (
                                        <p className="text-gray-500 dark:text-gray-400 text-xs py-2">
                                            {t('gym.dashboard.cards.dataUnavailable')}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </motion.section>

                        {/* Blocco unico: Allenamenti + Indice di continuità (a destra del secchio) */}
                        <motion.section
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.08 }}
                            className="md:col-span-2 xl:col-span-2 h-full min-h-[clamp(210px,26vh,300px)] rounded-2xl overflow-hidden flex flex-col border border-[#dad8e5] dark:border-white/10 bg-[#eeebf5] dark:bg-gray-900 shadow-lg dark:shadow-black/35"
                        >
                            <div className="px-5 py-4 bg-[#dad8e5] dark:bg-gray-800">
                                <h2 className="font-open-sans font-semibold text-[18px] sm:text-[20px] text-[#3a3a3a] dark:text-white">
                                    {t('gym.dashboard.workoutsTitle')}
                                </h2>
                            </div>
                            <div className="px-5 pt-4 pb-5 flex-1 min-h-0 bg-[#eeebf5] dark:bg-transparent">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
                                    {/* Colonna sinistra: mini box */}
                                    <div className="grid grid-cols-2 md:grid-cols-1 gap-3 md:gap-3">
                                        <div className="rounded-xl border border-[#dad8e5] dark:border-gray-700 bg-[#f5f3fa] dark:bg-gray-900 px-4 py-3">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="font-open-sans font-semibold uppercase tracking-wide text-[12px] text-[#3a3a3a] dark:text-white">
                                                    IN CORSO
                                                </div>
                                            </div>
                                            <div className="mt-1 flex justify-end">
                                                <div className="font-open-sans font-bold tabular-nums text-[26px] text-[#3a3a3a] dark:text-white leading-none">
                                                    <AnimatedNumber value={workoutsInCorso} duration={1} delay={0.2} className="inline" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="rounded-xl border border-[#dad8e5] dark:border-gray-700 bg-[#f5f3fa] dark:bg-gray-900 px-4 py-3">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="font-open-sans font-semibold uppercase tracking-wide text-[12px] text-[#3a3a3a] dark:text-white">
                                                    COMPLETATI OGGI
                                                </div>
                                            </div>
                                            <div className="mt-1 flex justify-end">
                                                <div className="font-open-sans font-bold tabular-nums text-[26px] text-[#3a3a3a] dark:text-white leading-none">
                                                    <AnimatedNumber value={workoutsDoneToday} duration={1} delay={0.25} className="inline" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-span-2 sm:col-span-1 rounded-xl border border-[#dad8e5] dark:border-gray-700 bg-[#e3deef] dark:bg-gray-950 px-4 py-3 flex flex-col items-center text-center">
                                            <div className="font-open-sans font-semibold uppercase tracking-wide text-[12px] text-[#3a3a3a] dark:text-white">
                                                FREQUENZA MEDIA
                                            </div>
                                            <div className="mt-2 font-open-sans font-extrabold tabular-nums text-[30px] text-[#3a3a3a] dark:text-white leading-none">
                                                {typeof snapshot?.frequenza_media_settimanale === 'number' ? (
                                                    <CountUp start={0} end={snapshot.frequenza_media_settimanale} duration={1.0} delay={0.25} decimals={1} />
                                                ) : (
                                                    '-'
                                                )}
                                            </div>
                                            <div className="mt-1 font-open-sans font-normal text-[12px] text-[#3a3a3a] dark:text-white/80">
                                                Allenamenti alla Settimana
                                            </div>
                                        </div>
                                    </div>

                                    {/* Destra: tachimetro indice continuità */}
                                    <div className="rounded-xl border border-[#dad8e5] dark:border-gray-700 bg-[#e3deef] dark:bg-gray-950 px-4 py-3 flex flex-col items-center text-center">
                                        <div className="font-open-sans font-semibold uppercase tracking-wide text-[12px] text-[#3a3a3a] dark:text-white">
                                            {t('gym.dashboard.attivitaAllenamento.indiceContinuita')}
                                        </div>
                                        <div className="flex-1 min-h-0 flex items-center justify-center">
                                            <ContinuityGauge
                                                pct={typeof snapshot?.indice_continuita_attivita === 'number' ? snapshot.indice_continuita_attivita : 0}
                                                label={
                                                    typeof snapshot?.indice_continuita_attivita === 'number'
                                                        ? stripPctRangePrefix(
                                                            t(`gym.dashboard.attivitaAllenamento.bands.${getAttivitaBandKey(snapshot.indice_continuita_attivita)}`)
                                                        )
                                                        : ''
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.section>
                    </div>

                    {/* Riga 2: Segnali da Monitorare + Clienti da contattare */}
                    <div className={`grid grid-cols-1 md:grid-cols-2 mt-5 md:mt-6 items-stretch ${isMobile ? 'gap-3' : 'gap-4 md:gap-6 xl:gap-8'}`}>
                        <motion.section
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.24 }}
                            className="rounded-2xl overflow-hidden min-h-0 flex flex-col border border-[#dad8e5] dark:border-white/10 bg-[#eeebf5] dark:bg-gray-900 shadow-lg dark:shadow-black/35"
                        >
                            <div className="px-5 bg-[#dad8e5] dark:bg-gray-800 h-14 sm:h-16 flex items-center">
                                <h2 className="font-open-sans font-semibold text-[18px] sm:text-[20px] text-[#3a3a3a] dark:text-white">
                                    {t('gym.dashboard.segnaliMonitorare.title')}
                                </h2>
                            </div>
                            <div className="px-5 pt-4 pb-5 flex-1 min-h-0 bg-[#eeebf5] dark:bg-transparent">
                                {snapshot != null && (typeof snapshot.clienti_frequenza_in_calo === 'number' || typeof snapshot.clienti_riattivati_mese === 'number') ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            title={t('gym.dashboard.listaCardModal.cliccaElenco')}
                                            onClick={() =>
                                                snapshot != null && typeof snapshot.clienti_frequenza_in_calo === 'number' && setListCardModal('frequenza_in_calo')
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    if (snapshot != null && typeof snapshot.clienti_frequenza_in_calo === 'number') {
                                                        setListCardModal('frequenza_in_calo');
                                                    }
                                                }
                                            }}
                                            className="rounded-xl px-4 py-4 flex items-center gap-4 border border-[#dad8e5] dark:border-white/10 cursor-pointer transition hover:brightness-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#372d83] dark:focus-visible:ring-white/50"
                                            style={{
                                                background: isDark
                                                    ? 'linear-gradient(90deg, #3d3667 0%, #c9171a 100%)'
                                                    : 'linear-gradient(90deg, #ede8f4 0%, #ca8e93 100%)',
                                            }}
                                        >
                                            <div className="w-full min-w-0">
                                                <div className="font-open-sans font-semibold text-[12px] text-[#3a3a3a] dark:text-white leading-tight">
                                                    {t('gym.dashboard.segnaliMonitorare.frequenzaInCalo')}
                                                </div>
                                                <div className="mt-3 relative flex items-center">
                                                    <div className="absolute left-0 shrink-0 text-[#b24c5b] dark:text-[#c9171a]">
                                                        <MonitorDownIcon className="w-10 h-10" />
                                                    </div>
                                                    <div className="mx-auto font-open-sans font-extrabold tabular-nums text-[40px] leading-none text-[#3a3a3a] dark:text-white">
                                                        {typeof snapshot.clienti_frequenza_in_calo === 'number' ? snapshot.clienti_frequenza_in_calo : '-'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            role="button"
                                            tabIndex={0}
                                            title={t('gym.dashboard.listaCardModal.cliccaElenco')}
                                            onClick={() =>
                                                snapshot != null && typeof snapshot.clienti_riattivati_mese === 'number' && setListCardModal('riattivati_mese')
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    if (snapshot != null && typeof snapshot.clienti_riattivati_mese === 'number') {
                                                        setListCardModal('riattivati_mese');
                                                    }
                                                }
                                            }}
                                            className="rounded-xl px-4 py-4 flex items-center gap-4 border border-[#dad8e5] dark:border-white/10 cursor-pointer transition hover:brightness-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#372d83] dark:focus-visible:ring-white/50"
                                            style={{
                                                background: isDark
                                                    ? 'linear-gradient(90deg, #3d3667 0%, #00cc99 100%)'
                                                    : 'linear-gradient(90deg, #ede8f4 0%, #00cc99 100%)',
                                            }}
                                        >
                                            <div className="w-full min-w-0">
                                                <div className="font-open-sans font-semibold text-[12px] text-[#3a3a3a] dark:text-white leading-tight">
                                                    {t('gym.dashboard.segnaliMonitorare.riattivatiMese')}
                                                </div>
                                                <div className="mt-3 relative flex items-center">
                                                    <div className="absolute left-0 shrink-0 text-[#a0d6d1] dark:text-[#00cc99]">
                                                        <MonitorUpIcon className="w-10 h-10" />
                                                    </div>
                                                    <div className="mx-auto font-open-sans font-extrabold tabular-nums text-[40px] leading-none text-[#3a3a3a] dark:text-white">
                                                        {typeof snapshot.clienti_riattivati_mese === 'number' ? snapshot.clienti_riattivati_mese : '-'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-[#3a3a3a] dark:text-white/80 text-sm py-2">
                                        {t('gym.dashboard.cards.dataUnavailable')}
                                    </p>
                                )}
                            </div>
                        </motion.section>

                        <motion.section
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.32 }}
                            className="rounded-2xl overflow-hidden min-h-0 flex flex-col border border-[#dad8e5] dark:border-white/10 bg-[#eeebf5] dark:bg-gray-900 shadow-lg dark:shadow-black/35"
                        >
                            <div className="px-5 bg-[#dad8e5] dark:bg-gray-800 h-14 sm:h-16 flex items-center">
                                <div className="flex items-center gap-3">
                                    <h2 className="font-open-sans font-semibold text-[18px] sm:text-[20px] text-[#3a3a3a] dark:text-white">
                                        {t('gym.dashboard.cards.daContattare')}
                                    </h2>
                                    {snapshot != null && typeof snapshot.clienti_da_contattare === 'number' && snapshot.clienti_da_contattare > 0 && (
                                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#c9171a] text-white font-open-sans font-extrabold tabular-nums text-[18px] leading-none">
                                            {snapshot.clienti_da_contattare}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="px-5 pt-5 pb-6 flex-1 min-h-0 flex items-center bg-[#eeebf5] dark:bg-transparent">
                            {snapshot == null ? (
                                <p className="text-[#3a3a3a] dark:text-white/80 text-sm py-2">
                                    {t('gym.dashboard.cards.dataUnavailable')}
                                </p>
                            ) : (
                                <div className="w-full flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                                    <div className="flex items-center justify-center md:justify-start gap-5 min-w-0">
                                        <div className="shrink-0 text-[#bca8d1] dark:text-[#a6a1bd] opacity-90">
                                            <ContattareTelefonoIcon className="w-20 h-20" />
                                        </div>
                                        <div className="font-open-sans font-extrabold tabular-nums text-[#3a3a3a] dark:text-white text-[64px] leading-none">
                                            <AnimatedNumber value={snapshot.clienti_da_contattare} duration={1} delay={0.6} className="inline" />
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => setDaContattareOpen(true)}
                                        className="mx-auto md:mx-0 shrink-0 font-open-sans font-semibold text-[#3a3a3a] bg-[#bca8d1] hover:brightness-95 dark:text-white dark:bg-gray-900 dark:hover:brightness-110 rounded-xl px-10 py-4 text-[18px] shadow-lg shadow-black/20 transition"
                                    >
                                        {t('gym.dashboard.cards.vediLista')}
                                    </button>
                                </div>
                            )}
                        </div>
                        </motion.section>
                    </div>
                </div>
            </div>

            {/* Modale Clienti da contattare - dark: solo tabella a tutta larghezza su #382e84 (niente pannello interno); azione/note #382e84 */}
            <Dialog open={daContattareOpen} onOpenChange={setDaContattareOpen}>
                <DialogContent className="max-h-[85vh] max-w-[min(100vw-1.5rem,46.2rem)] gap-0 overflow-hidden rounded-2xl border border-gray-200 bg-white p-0 shadow-lg dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35 flex flex-col">
                    <div className="shrink-0 flex min-h-14 sm:min-h-16 items-center bg-[#dad8e5] px-5 pr-12 dark:bg-gray-800">
                        <DialogHeader className="space-y-0 text-left">
                            <DialogTitle className="font-open-sans text-[18px] font-semibold text-[#3a3a3a] dark:text-white sm:text-[20px]">
                                {t('gym.dashboard.daContattareModal.title')}
                            </DialogTitle>
                        </DialogHeader>
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto bg-[#eeebf5] px-5 py-4 sm:py-5 dark:bg-gray-900 dark:px-0 dark:pt-0 dark:pb-3 dark:sm:pb-4">
                        {loadingListaDaContattare ? (
                            <p className="font-open-sans py-4 text-sm text-[#3a3a3a] dark:px-5 dark:text-white/80">{t('gym.dashboard.loading')}</p>
                        ) : listaDaContattare.length === 0 ? (
                            <p className="font-open-sans py-4 text-sm text-[#3a3a3a] dark:px-5 dark:text-white/80">{t('gym.dashboard.daContattareModal.nessunCliente')}</p>
                        ) : (
                            <div className="overflow-hidden rounded-xl border border-[#dad8e5] dark:rounded-none dark:border-0">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-100 dark:bg-gray-800">
                                        <tr>
                                            <th className="px-3 py-2.5 text-left font-open-sans text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-white">
                                                {t('gym.dashboard.daContattareModal.nome')}
                                            </th>
                                            <th className="min-w-[11.5rem] px-3 py-2.5 text-left font-open-sans text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-white sm:min-w-[12.5rem]">
                                                {t('gym.dashboard.daContattareModal.stato')}
                                            </th>
                                            <th className="px-3 py-2.5 text-left font-open-sans text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-white">
                                                {t('gym.dashboard.daContattareModal.azione')}
                                            </th>
                                            <th className="px-3 py-2.5 text-left font-open-sans text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-white">
                                                {t('gym.dashboard.daContattareModal.note')}
                                            </th>
                                            <th className="w-24" />
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-white/10 dark:bg-gray-900">
                                        {listaDaContattare.map((item) => (
                                            <tr key={item.user_id} className="bg-white transition-colors dark:bg-transparent hover:bg-gray-50 dark:hover:bg-white/[0.08]">
                                                <td className="px-3 py-2.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setDaContattareOpen(false);
                                                            navigate(`/gym/members?memberId=${encodeURIComponent(String(item.user_id))}`);
                                                        }}
                                                        className="text-left font-open-sans font-semibold text-violet-700 hover:underline dark:text-gray-300 dark:hover:text-white"
                                                        title={t('gym.navigation.members')}
                                                    >
                                                        {item.nome}
                                                    </button>
                                                </td>
                                                <td className="min-w-[11.5rem] px-3 py-2.5 sm:min-w-[12.5rem]">
                                                    <span
                                                        className={`inline-block max-w-full rounded px-2 py-0.5 text-xs font-medium whitespace-normal text-white sm:whitespace-nowrap ${
                                                            item.stato === 'rischio_abbandono' ? 'bg-[#c9171a]' : 'bg-[#ea6849]'
                                                        }`}
                                                    >
                                                        {item.stato === 'rischio_abbandono'
                                                            ? t('gym.dashboard.daContattareModal.rischioAbbandono')
                                                            : t('gym.dashboard.daContattareModal.inRallentamento')}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    <select
                                                        value={formDaContattare[item.user_id]?.tipo_azione ?? ''}
                                                        onChange={(e) =>
                                                            setFormDaContattare((prev) => ({
                                                                ...prev,
                                                                [item.user_id]: { ...(prev[item.user_id] || { note: '' }), tipo_azione: e.target.value },
                                                            }))
                                                        }
                                                        className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 font-open-sans text-sm text-gray-900 dark:border-white/20 dark:bg-gray-900 dark:text-white"
                                                    >
                                                        <option value="">{t('gym.dashboard.daContattareModal.selezionaAzione')}</option>
                                                        <option value="chiamata">{t('gym.dashboard.daContattareModal.chiamata')}</option>
                                                        <option value="messaggio">{t('gym.dashboard.daContattareModal.messaggio')}</option>
                                                        <option value="invito_allenamento">{t('gym.dashboard.daContattareModal.invitoAllenamento')}</option>
                                                        <option value="modifica_scheda">{t('gym.dashboard.daContattareModal.modificaScheda')}</option>
                                                        <option value="colloquio">{t('gym.dashboard.daContattareModal.colloquio')}</option>
                                                    </select>
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    <input
                                                        type="text"
                                                        placeholder={t('gym.dashboard.daContattareModal.note')}
                                                        value={formDaContattare[item.user_id]?.note || ''}
                                                        onChange={(e) =>
                                                            setFormDaContattare((prev) => ({
                                                                ...prev,
                                                                [item.user_id]: { ...(prev[item.user_id] || { tipo_azione: '' }), note: e.target.value },
                                                            }))
                                                        }
                                                        className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 font-open-sans text-sm text-gray-900 placeholder:text-gray-400 dark:border-white/20 dark:bg-gray-900 dark:text-white dark:placeholder:text-white/50"
                                                    />
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    <button
                                                        type="button"
                                                        disabled={submittingContact === item.user_id || !formDaContattare[item.user_id]?.tipo_azione}
                                                        onClick={() => handleRegistraContatto(item.user_id)}
                                                        className="font-open-sans text-xs font-semibold text-violet-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-white/90 dark:hover:text-white"
                                                    >
                                                        {submittingContact === item.user_id ? '...' : t('gym.dashboard.daContattareModal.registra')}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modale lista In Rallentamento / Rischio - stesso pattern modale Clienti da contattare (dark) */}
            <Dialog open={listCardModal !== null} onOpenChange={(open) => !open && setListCardModal(null)}>
                <DialogContent className="flex max-h-[85vh] max-w-[min(100vw-1.5rem,28rem)] flex-col gap-0 overflow-hidden rounded-2xl border border-gray-200 bg-white p-0 shadow-lg dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35">
                    <div className="flex min-h-14 shrink-0 items-center bg-[#dad8e5] px-5 pr-12 sm:min-h-16 dark:bg-gray-800">
                        <DialogHeader className="space-y-0 text-left">
                            <DialogTitle className="font-open-sans text-[18px] font-semibold text-[#3a3a3a] dark:text-white sm:text-[20px]">
                                {listCardModal === 'rallentamento' && t('gym.dashboard.cards.inRallentamento')}
                                {listCardModal === 'rischio' && t('gym.dashboard.cards.rischioAbbandono')}
                                {listCardModal === 'frequenza_in_calo' && t('gym.dashboard.segnaliMonitorare.frequenzaInCalo')}
                                {listCardModal === 'riattivati_mese' && t('gym.dashboard.segnaliMonitorare.riattivatiMese')}
                            </DialogTitle>
                        </DialogHeader>
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto bg-[#eeebf5] px-5 py-4 sm:py-5 dark:bg-gray-900 dark:px-0 dark:pt-0 dark:pb-3 dark:sm:pb-4">
                        {loadingListCard ? (
                            <p className="font-open-sans py-4 text-sm text-[#3a3a3a] dark:px-5 dark:text-white/80">{t('gym.dashboard.loading')}</p>
                        ) : listCardData.length === 0 ? (
                            <p className="font-open-sans py-4 text-sm text-[#3a3a3a] dark:px-5 dark:text-white/80">{t('gym.dashboard.listaCardModal.nessunCliente')}</p>
                        ) : (
                            <div className="overflow-hidden rounded-xl border border-[#dad8e5] dark:rounded-none dark:border-0">
                                <ul className="divide-y divide-gray-200 bg-white dark:divide-white/10 dark:bg-gray-900">
                                    {listCardData.map((item) => (
                                        <li
                                            key={item.user_id}
                                            className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3 font-open-sans text-sm text-gray-900 dark:bg-transparent dark:text-white"
                                        >
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setListCardModal(null);
                                                    navigate(`/gym/members?memberId=${encodeURIComponent(String(item.user_id))}`);
                                                }}
                                                className="text-left font-semibold text-violet-700 hover:underline dark:text-gray-300 dark:hover:text-white"
                                                title={t('gym.navigation.members')}
                                            >
                                                {item.nome || 'ID ' + item.user_id}
                                            </button>
                                            {'ultimo_allenamento' in item && item.ultimo_allenamento ? (
                                                <span className="text-xs text-gray-600 dark:text-white/70">
                                                    {t('gym.dashboard.listaCardModal.ultimoAllenamento')}:{' '}
                                                    {new Date(item.ultimo_allenamento).toLocaleDateString('it-IT', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric',
                                                    })}
                                                </span>
                                            ) : 'ultimo_allenamento' in item ? (
                                                <span className="text-xs text-gray-400 dark:text-white/50">-</span>
                                            ) : null}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default GymDashboard;
